import type { ReactElement } from "react";
import { Link } from "@tanstack/react-router";

import { cn } from "@/lib/utils";

import { formatCredits, type CreditBalance } from "./api";

/**
 * Compact remaining-credits readout. Lives in the workspace header and links
 * through to the full billing page, mirroring Lovable's credit bar.
 */
export function CreditBar({
  balance,
  className,
}: {
  balance: CreditBalance;
  className?: string;
}): ReactElement {
  // The bar is drawn against the plan's monthly allocation. Free accounts have
  // no allocation, so their daily credits become the denominator — otherwise
  // the bar would divide by zero and always read empty.
  const capacity =
    balance.monthlyCredits > 0
      ? balance.monthlyCredits
      : Math.max(balance.totalCredits, 5);
  const pct = Math.max(0, Math.min(100, (balance.totalCredits / capacity) * 100));
  const low = balance.totalCredits <= capacity * 0.1;

  return (
    <Link
      to="/billing"
      className={cn(
        "group flex w-full max-w-64 flex-col gap-1.5 rounded-lg px-3 py-2 transition hover:bg-black/5",
        className,
      )}
    >
      <div className="flex items-baseline justify-between gap-3 text-xs">
        <span className="font-medium text-[#171816]">
          {formatCredits(balance.totalCredits)} credits
        </span>
        <span className="text-[#74766f] group-hover:text-[#20272b]">
          {balance.planLabel}
        </span>
      </div>
      <div
        className="h-1.5 overflow-hidden rounded-full bg-[#e2e2dc]"
        role="progressbar"
        aria-valuenow={Math.round(pct)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Credits remaining"
      >
        <div
          className={cn(
            "h-full rounded-full transition-all",
            low ? "bg-[#b4482f]" : "bg-[#20272b]",
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
    </Link>
  );
}
